import { ContractStatusType, UpdateContractStatusPayload } from '@/types/contract/contract_status'
import { GetDocumentsCountResponse } from '@/types/documents'
import axios from 'axios'
import nookies from 'nookies'

const api_url = process.env.NEXT_PUBLIC_API_URL
const contracts_url = api_url + '/contracts'
const payments_url = api_url + '/payments'
let config: any = {}

const token = nookies.get(null, 'session')
if (token) {
  config = {
    headers: {
      Authorization: 'Bearer ' + token.session,
    },
  }
}

function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

const updateContract = async (requestData: Record<string, any>, contract_id: string | number): Promise<any> => {
  const { data } = await axios.put(contracts_url + `/` + contract_id, requestData)
  return data
}

const preCalculateContract = async (requestData: Record<string, any>): Promise<any> => {
  const { data } = await axios.post(contracts_url + `/pre-calculate/`, requestData)
  return data
}

const saveCustomerData = async (id: string, requestData: Record<string, any>): Promise<any> => {
  const { data } = await axios.post(contracts_url + `/${id}/customer_data/`, requestData)
  return data
}

const saveRenterData = async (id: string, requestData: Record<string, any>): Promise<any> => {
  const { data } = await axios.post(contracts_url + `/${id}/renter/`, requestData)
  return data
}

const saveCoRenterData = async (id: string, requestData: Record<string, any>): Promise<any> => {
  const { data } = await axios.post(contracts_url + `/${id}/co-renter/`, requestData)
  return data
}

const saveReferences = async (id: string, requestData: Record<string, any>): Promise<any> => {
  const { data } = await axios.post(contracts_url + `/${id}/reference/`, requestData)
  return data
}

const signLater = async (id: string | string[]): Promise<any> => {
  const { data } = await axios.post(contracts_url + `/${id}/sign_later`)
  return data
}

const sendContract = async (id: string | string[]): Promise<any> => {
  const { data } = await axios.post(contracts_url + `/${id}/send`)
  return data
}

const signLaterRenter = async (id: string | string[]): Promise<any> => {
  const { data } = await axios.post(contracts_url + `/${id}/sign_later_renter`)
  return data
}

const signLaterCorenter = async (id: string | string[]): Promise<any> => {
  const { data } = await axios.post(contracts_url + `/${id}/sign_later_co_renter`)
  return data
}

const deleteCorenter = async (id: string | string[]): Promise<any> => {
  const { data } = await axios.delete(contracts_url + `/${id}/co-renter`)
  return data
}

const acceptContinueContract = async (id: string | string[]): Promise<any> => {
  const { data } = await axios.post(contracts_url + `/${id}/accept_child_contract`)
  return data
}

const getStatesdata = async (): Promise<any> => {
  const { data } = await axios.get(api_url + `/state/`)
  return data
}

const getManufactureTerms = async (external_hash: string | string[]): Promise<any> => {
  const { data } = await axios.get(contracts_url + `/${external_hash}/manufacture_terms`)
  return data
}

const updateContractConfiguration = async (
  requestData: Record<string, any>,
  contract_id: string
): Promise<any> => {
  const { data } = await axios.put(contracts_url + `/${contract_id}/update_configuration`, requestData)
  return data
}

const getContracts = async (params: string, controller: AbortController): Promise<any> => {
  const url = `${contracts_url}/${params || ''}`

  const { data } = await axios.get(url, { signal: controller.signal })

  return data
}

const getDocumentTypes = async (uuid: string): Promise<any> => {
  const url = `${contracts_url}/${uuid || ''}/document_types`
  const { data } = await axios.get(url)
  return data
}

const getDocuments = async (uuid: string): Promise<any> => {
  const url = `${contracts_url}/${uuid || ''}/documents`
  const { data } = await axios.get(url)
  return data
}

const deleteDocument = async (contractId: string, documentId: string): Promise<any> => {
  const url = `${contracts_url}/${contractId || ''}/document/${documentId || ''}`
  const { data } = await axios.delete(url)
  return data
}

const contractDetails = async (uuid: string | string[]): Promise<any> => {
  const url = `${contracts_url}/${uuid || ''}/details`
  await delay(1000)
  const { data } = await axios.get(url)
  return data
}

const addContractNote = async (uuid: string | string[], note: string): Promise<any> => {
  const { data } = await axios.post(contracts_url + `/${uuid}/notes`, {
    note: note,
  })
  return data
}

const getContractNotes = async (
  uuid: string | string[],
  page_size: number,
  page_number: number
): Promise<any> => {
  const url = `${contracts_url}/${uuid || ''}/notes?page_number=${page_number}&page_size=${page_size}`
  const { data } = await axios.get(url)
  return data
}

const resendLink_signLater = async (uuid: string | string[]): Promise<any> => {
  const { data } = await axios.post(contracts_url + `/${uuid}/resend-sign-later-emails`)
  return data
}

const getContractHistory = async (
  uuid: string | string[],
  page_size: number,
  page_number: number
): Promise<any> => {
  const url = `${contracts_url}/${uuid || ''}/audits?page_number=${page_number || 1}&page_size=${page_size || 5}`
  const { data } = await axios.get(url)
  return data
}

const getUserAgreementStatusConfig: any = async () => {
  const { data } = await axios.get(`${contracts_url}/user_agreement_status_config`, config)

  return data
}

interface AllowedStatus {
  id: number
  description: string
  logo: string
  access_direct: boolean
  undo: true
}

type GetAllowedStatuses = (agreementId: string | string[]) => Promise<{
  data: AllowedStatus[]
  message: string
}>

const getAllowedStatuses: GetAllowedStatuses = async (agreementId) => {
  const { data } = await axios.get(`${contracts_url}/${agreementId}/allowed_statuses`, config)
  return data
}

const updateAgreementStatus = async (agreementId: string | string[], statusId: number): Promise<any> => {
  const { data } = await axios.post(
    `${contracts_url}/${agreementId}/update_status`,
    { agreement_status_id: statusId },
    config
  )
  return data
}

const duplicateContract = async (uuid: string | string[]): Promise<any> => {
  const url = `${contracts_url}/${uuid || ''}/duplicate`
  const { data } = await axios.post(url)
  return data
}

const getContractStatus = async () : Promise<ContractStatusType[]> => {
  const { data } = await axios.get(`${contracts_url}/agreement-status`, config)
  return data.data
}

const updateContractStatus = async (payload: UpdateContractStatusPayload) : Promise<void> => {
  const req_data = {agreement_status_id: payload.agreement_status_id}
  const { data } = await axios.post(`${contracts_url}/${payload.uuid}/agreement_status_change`, req_data, config)
  return data
}

const addPaymentMethod = async (
  contractId: string,
  payment_method_id: string,
  tp_token: string,
  matic_gateway_id: string
): Promise<any> => {
  const url = `${payments_url}/${contractId}/payment_method`
  const { data } = await axios.post(
    url,
    { payment_method_id, tp_token: tp_token || null, matic_gateway_id },
    config
  )
  return data
}

const getGpsManufacturers = async (): Promise<any> => {
  const response = await axios.get(contracts_url + `/gps-manufacturer`, config)
  const { data } = response.data
  return data
}
const getCheckIfHasDocumentsService = async (contractId: string): Promise<GetDocumentsCountResponse> => {
  const response = await axios.get(contracts_url + `/${contractId}/documents/count`, config)
  const { data } = response.data
  return data
}

const postExportDocuments = async (contractId: string): Promise<string> => {
  const response = await axios.post(contracts_url + `/${contractId}/documents/export`, null, config)
  const { data } = response.data
  return data
}

const verifyUserAccess = async (contractId: string): Promise<string> => {
  const response = await axios.get(
    contracts_url + `/verify-user-access?work_order_external_hash=${contractId}`,
    config
  )
  const { data } = response.data
  return data
}

const getAmountToBeIssue = async (agreementId: string): Promise<any> => {
  const response = await axios.get(contracts_url + `/${agreementId}/credit-to-be-issued`, config)
  const { data } = response.data
  return data
}
export default {
  getCheckIfHasDocumentsService,
  postExportDocuments,
  updateContract,
  saveCustomerData,
  preCalculateContract,
  sendContract,
  saveRenterData,
  saveCoRenterData,
  saveReferences,
  signLaterRenter,
  signLaterCorenter,
  signLater,
  deleteCorenter,
  acceptContinueContract,
  getStatesdata,
  getContracts,
  getManufactureTerms,
  updateContractConfiguration,
  getDocumentTypes,
  getDocuments,
  contractDetails,
  addContractNote,
  getContractNotes,
  deleteDocument,
  getContractHistory,
  config,
  resendLink_signLater,
  getUserAgreementStatusConfig,
  getAllowedStatuses,
  updateAgreementStatus,
  duplicateContract,
  addPaymentMethod,
  getGpsManufacturers,
  verifyUserAccess,
  getAmountToBeIssue,
  getContractStatus,
  updateContractStatus
}
